import { isWorldServerData, type WorldServerData } from "@endclock/shared";

const unavailable: WorldServerData = { cpuPercent: null, ramPercent: null, diskPercent: null };
const metrics = ["cpuPercent", "ramPercent", "diskPercent"] as const;

function utilizationColor(percent: number): string {
  const stops = [
    [0, 102, 165, 112],
    [50, 213, 190, 83],
    [75, 216, 134, 64],
    [100, 199, 84, 76],
  ] as const;
  const upper = stops.findIndex((stop, index) => index > 0 && percent <= stop[0]);
  const end = stops[upper < 0 ? 3 : upper]!;
  const start = stops[Math.max(0, (upper < 0 ? 3 : upper) - 1)]!;
  const fraction = (percent - start[0]) / (end[0] - start[0]);
  return `rgb(${[1, 2, 3].map(channel => Math.round(start[channel]! + (end[channel]! - start[channel]!) * fraction)).join(", ")})`;
}

/** A separate, best-effort HUD read; no gameplay messages or render-loop work. */
export function setupWorldServerData(): void {
  const panel = document.querySelector<HTMLDetailsElement>("#world-server-data");
  if (!panel) return;
  let request: AbortController | undefined;
  let suspended = false;
  let timer: number | undefined;
  let generation = 0;

  const render = (data: WorldServerData): void => {
    for (const key of metrics) {
      const row = panel.querySelector<HTMLElement>(`[data-metric="${key}"]`);
      const bar = row?.querySelector<HTMLElement>("[role='meter']");
      const fill = bar?.querySelector<HTMLElement>("i");
      const value = row?.querySelector<HTMLOutputElement>("output");
      if (!bar || !fill || !value) continue;
      const percent = data[key];
      value.textContent = percent === null ? "—" : `${Math.round(percent)}%`;
      fill.style.transform = `scaleX(${percent === null ? 0 : percent / 100})`;
      fill.style.backgroundColor = percent === null ? "transparent" : utilizationColor(percent);
      if (percent === null) bar.removeAttribute("aria-valuenow");
      else bar.setAttribute("aria-valuenow", String(Math.round(percent)));
      bar.setAttribute("aria-valuetext", value.textContent);
    }
  };

  const refresh = async (): Promise<void> => {
    if (!panel.open || document.hidden || suspended || request) return;
    window.clearTimeout(timer);
    timer = undefined;
    const owner = generation;
    const controller = new AbortController();
    request = controller;
    const timeout = window.setTimeout(() => controller.abort(), 2_000);
    try {
      const response = await fetch("/api/world-server-data", {
        method: "GET", credentials: "same-origin", cache: "no-store", signal: controller.signal,
      });
      if (owner !== generation) return;
      if (!response.ok) { render(unavailable); return; }
      const data: unknown = await response.json();
      if (owner === generation) render(isWorldServerData(data) ? data : unavailable);
    } catch {
      if (owner === generation) render(unavailable);
    } finally {
      window.clearTimeout(timeout);
      if (owner === generation) {
        request = undefined;
        if (panel.open && !document.hidden && !suspended) timer = window.setTimeout(() => { void refresh(); }, 3_000);
      }
    }
  };

  const stop = (): void => {
    generation += 1;
    window.clearTimeout(timer);
    timer = undefined;
    request?.abort();
    request = undefined;
  };

  render(unavailable);
  panel.addEventListener("toggle", () => {
    if (panel.open) void refresh();
    else stop();
  });
  // Let native summary keyboard activation run without the game's Space handler.
  panel.querySelector("summary")?.addEventListener("keydown", event => {
    if (event.code === "Space" || event.code === "Enter") event.stopPropagation();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) { stop(); render(unavailable); }
    else void refresh();
  });
  window.addEventListener("pagehide", () => {
    suspended = true;
    stop();
  });
  window.addEventListener("pageshow", () => {
    if (!suspended) return;
    suspended = false;
    void refresh();
  });
  if (panel.open) void refresh();
}
